// src/models/HolidayModels.js

/**
 * Holiday Management Models
 * Port of Holiday.swift for React web application
 */

import { v4 as uuidv4 } from 'uuid';

// MARK: - Error Types
export class HolidayServiceError extends Error {
  constructor(type, message, originalError = null) {
    super(message);
    this.name = 'HolidayServiceError';
    this.type = type;
    this.originalError = originalError;
  }

  static get types() {
    return {
      INVALID_COMPANY_ID: 'INVALID_COMPANY_ID',
      NO_INTERNET_CONNECTION: 'NO_INTERNET_CONNECTION',
      UNAUTHORIZED: 'UNAUTHORIZED',
      DATA_CORRUPTION: 'DATA_CORRUPTION',
      INVALID_DATE: 'INVALID_DATE',
      INVALID_NAME: 'INVALID_NAME',
      DUPLICATE_HOLIDAY: 'DUPLICATE_HOLIDAY',
      UNKNOWN: 'UNKNOWN'
    };
  }

  static create(type, originalError = null) {
    const messages = {
      [this.types.INVALID_COMPANY_ID]: 'Invalid company ID provided',
      [this.types.NO_INTERNET_CONNECTION]: 'No internet connection available',
      [this.types.UNAUTHORIZED]: 'You do not have permission to perform this action',
      [this.types.DATA_CORRUPTION]: 'Data could not be processed correctly',
      [this.types.INVALID_DATE]: 'Invalid date provided',
      [this.types.INVALID_NAME]: 'Holiday name is required and cannot be empty',
      [this.types.DUPLICATE_HOLIDAY]: 'A holiday already exists on this date',
      [this.types.UNKNOWN]: originalError?.message || 'An unknown error occurred'
    };

    return new HolidayServiceError(type, messages[type], originalError);
  }
}

// MARK: - Holiday Validation
export class HolidayValidator {
  static validateName(name) {
    if (!name || typeof name !== 'string') {
      return { isValid: false, error: 'Holiday name is required' };
    }
    
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      return { isValid: false, error: 'Holiday name cannot be empty' };
    }
    
    if (trimmedName.length > 100) {
      return { isValid: false, error: 'Holiday name cannot exceed 100 characters' };
    }
    
    return { isValid: true };
  }

  static validateDate(date) {
    if (!date || !(date instanceof Date)) {
      return { isValid: false, error: 'Valid date is required' };
    }
    
    if (isNaN(date.getTime())) {
      return { isValid: false, error: 'Invalid date provided' };
    }
    
    // Check if date is too far in the past or future (reasonable business limits)
    const minYear = 2000;
    const maxYear = 2100;
    const year = date.getFullYear();
    
    if (year < minYear || year > maxYear) {
      return { isValid: false, error: `Year must be between ${minYear} and ${maxYear}` };
    }
    
    return { isValid: true };
  }

  static validateHoliday(holiday) {
    const nameValidation = this.validateName(holiday.name);
    if (!nameValidation.isValid) {
      return nameValidation;
    }
    
    const dateValidation = this.validateDate(holiday.date);
    if (!dateValidation.isValid) {
      return dateValidation;
    }
    
    if (typeof holiday.isPaidHoliday !== 'boolean') {
      return { isValid: false, error: 'isPaidHoliday must be a boolean value' };
    }
    
    return { isValid: true };
  }
}

// MARK: - Holiday Model
export class Holiday {
  constructor({
    id = null,
    name = '',
    date = new Date(),
    isPaidHoliday = true
  } = {}) {
    this.id = id || uuidv4();
    this.name = name;
    this.date = new Date(date); // Ensure we have a Date object
    this.isPaidHoliday = isPaidHoliday;
  }

  // Computed property for Firestore storage
  get dateString() {
    return Holiday.formatDateForStorage(this.date);
  }

  // Create a copy of this holiday
  clone() {
    return new Holiday({
      id: this.id,
      name: this.name,
      date: new Date(this.date),
      isPaidHoliday: this.isPaidHoliday
    });
  }

  // Validate this holiday instance
  validate() {
    return HolidayValidator.validateHoliday(this);
  }

  // Convert to Firestore format
  toFirestore() {
    const validation = this.validate();
    if (!validation.isValid) {
      throw HolidayServiceError.create(
        HolidayServiceError.types.DATA_CORRUPTION,
        new Error(validation.error)
      );
    }

    return {
      name: this.name.trim(),
      dateString: this.dateString,
      isPaidHoliday: this.isPaidHoliday
    };
  }

  // Create from Firestore data
  static fromFirestore(data, id) {
    try {
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid Firestore data');
      }

      const { name, dateString, isPaidHoliday } = data;

      if (!name || typeof name !== 'string') {
        throw new Error('Missing or invalid holiday name');
      }

      if (!dateString || typeof dateString !== 'string') {
        throw new Error('Missing or invalid holiday date string');
      }

      const date = Holiday.parseDateFromStorage(dateString);
      if (!date) {
        throw new Error('Could not parse holiday date');
      }

      return new Holiday({
        id,
        name,
        date,
        isPaidHoliday: isPaidHoliday !== undefined ? Boolean(isPaidHoliday) : true
      });
    } catch (error) {
      console.error('Error creating Holiday from Firestore data:', error);
      return null;
    }
  }

  // Static date formatting methods
  static formatDateForStorage(date) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      throw new Error('Invalid date provided for formatting');
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }

  static parseDateFromStorage(dateString) {
    if (!dateString || typeof dateString !== 'string') {
      return null;
    }

    // Parse yyyy-MM-dd format
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return null;
    }

    const [, year, month, day] = match;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

    // Verify the date is valid and matches what we parsed
    if (isNaN(date.getTime()) || 
        date.getFullYear() !== parseInt(year) ||
        date.getMonth() !== parseInt(month) - 1 ||
        date.getDate() !== parseInt(day)) {
      return null;
    }

    return date;
  }

  // Format date for display
  static formatDateForDisplay(date, locale = 'en-US') {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Format date for input fields
  static formatDateForInput(date) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }

    return Holiday.formatDateForStorage(date);
  }

  // Create common holidays for a given year
  static createCommonHolidays(year, locale = 'de') {
    if (!year || year < 2000 || year > 2100) {
      throw new Error('Invalid year provided');
    }

    const holidays = [];

    try {
      // Define common holidays based on locale
      if (locale === 'de') {
        // German holidays
        holidays.push(
          new Holiday({
            name: 'Neujahr',
            date: new Date(year, 0, 1), // January 1st
            isPaidHoliday: true
          }),
          new Holiday({
            name: 'Tag der Arbeit',
            date: new Date(year, 4, 1), // May 1st
            isPaidHoliday: true
          }),
          new Holiday({
            name: 'Tag der Deutschen Einheit',
            date: new Date(year, 9, 3), // October 3rd
            isPaidHoliday: true
          }),
          new Holiday({
            name: 'Allerheiligen',
            date: new Date(year, 10, 1), // November 1st
            isPaidHoliday: true
          }),
          new Holiday({
            name: 'Erster Weihnachtstag',
            date: new Date(year, 11, 25), // December 25th
            isPaidHoliday: true
          }),
          new Holiday({
            name: 'Zweiter Weihnachtstag',
            date: new Date(year, 11, 26), // December 26th
            isPaidHoliday: true
          })
        );

        // Add Easter-dependent holidays
        const easterDate = Holiday.calculateEasterDate(year);
        if (easterDate) {
          holidays.push(
            new Holiday({
              name: 'Ostermontag',
              date: new Date(easterDate.getTime() + (24 * 60 * 60 * 1000)), // Easter Monday
              isPaidHoliday: true
            }),
            new Holiday({
              name: 'Christi Himmelfahrt',
              date: new Date(easterDate.getTime() + (39 * 24 * 60 * 60 * 1000)), // Ascension Day (39 days after Easter)
              isPaidHoliday: true
            }),
            new Holiday({
              name: 'Pfingstmontag',
              date: new Date(easterDate.getTime() + (50 * 24 * 60 * 60 * 1000)), // Whit Monday (50 days after Easter)
              isPaidHoliday: true
            }),
            new Holiday({
              name: 'Fronleichnam',
              date: new Date(easterDate.getTime() + (60 * 24 * 60 * 60 * 1000)), // Corpus Christi (60 days after Easter)
              isPaidHoliday: true
            })
          );
        }

        // Mariä Himmelfahrt (August 15th)
        holidays.push(
          new Holiday({
            name: 'Mariä Himmelfahrt',
            date: new Date(year, 7, 15), // August 15th
            isPaidHoliday: true
          })
        );

      } else {
        // Default US holidays
        holidays.push(
          new Holiday({
            name: "New Year's Day",
            date: new Date(year, 0, 1), // January 1st
            isPaidHoliday: true
          }),
          new Holiday({
            name: 'Independence Day',
            date: new Date(year, 6, 4), // July 4th
            isPaidHoliday: true
          }),
          new Holiday({
            name: 'Christmas Day',
            date: new Date(year, 11, 25), // December 25th
            isPaidHoliday: true
          })
        );
      }

      // Filter out any invalid holidays
      return holidays.filter(holiday => {
        const validation = holiday.validate();
        return validation.isValid;
      });

    } catch (error) {
      console.error('Error creating common holidays:', error);
      return [];
    }
  }

  // Calculate Easter date for a given year (for German holidays)
  static calculateEasterDate(year) {
    try {
      // Using the algorithm for Western Easter calculation
      const a = year % 19;
      const b = Math.floor(year / 100);
      const c = year % 100;
      const d = Math.floor(b / 4);
      const e = b % 4;
      const f = Math.floor((b + 8) / 25);
      const g = Math.floor((b - f + 1) / 3);
      const h = (19 * a + b - d - g + 15) % 30;
      const i = Math.floor(c / 4);
      const k = c % 4;
      const l = (32 + 2 * e + 2 * i - h - k) % 7;
      const m = Math.floor((a + 11 * h + 22 * l) / 451);
      const month = Math.floor((h + l - 7 * m + 114) / 31);
      const day = ((h + l - 7 * m + 114) % 31) + 1;

      return new Date(year, month - 1, day);
    } catch (error) {
      console.error('Error calculating Easter date:', error);
      return null;
    }
  }

  // Helper method to check if two holidays are on the same date
  static isSameDate(holiday1, holiday2) {
    if (!holiday1 || !holiday2) return false;
    return holiday1.dateString === holiday2.dateString;
  }

  // Helper method to sort holidays by date
  static sortByDate(holidays, ascending = true) {
    if (!Array.isArray(holidays)) return [];
    
    return holidays.sort((a, b) => {
      const dateA = a.date.getTime();
      const dateB = b.date.getTime();
      return ascending ? dateA - dateB : dateB - dateA;
    });
  }

  // Helper method to filter holidays by year
  static filterByYear(holidays, year) {
    if (!Array.isArray(holidays)) return [];
    
    return holidays.filter(holiday => {
      return holiday.date.getFullYear() === year;
    });
  }

  // Helper method to filter holidays by month
  static filterByMonth(holidays, year, month) {
    if (!Array.isArray(holidays)) return [];
    
    return holidays.filter(holiday => {
      return holiday.date.getFullYear() === year && 
             holiday.date.getMonth() === month;
    });
  }

  // Helper method to get holidays within date range
  static filterByDateRange(holidays, startDate, endDate) {
    if (!Array.isArray(holidays) || !startDate || !endDate) return [];
    
    const start = startDate.getTime();
    const end = endDate.getTime();
    
    return holidays.filter(holiday => {
      const holidayTime = holiday.date.getTime();
      return holidayTime >= start && holidayTime <= end;
    });
  }
}

// MARK: - Holiday Collection Helper
export class HolidayCollection {
  constructor(holidays = []) {
    this.holidays = holidays.filter(h => h instanceof Holiday);
  }

  // Add holiday with duplicate check
  add(holiday) {
    if (!(holiday instanceof Holiday)) {
      throw new Error('Can only add Holiday instances');
    }

    const validation = holiday.validate();
    if (!validation.isValid) {
      throw HolidayServiceError.create(
        HolidayServiceError.types.DATA_CORRUPTION,
        new Error(validation.error)
      );
    }

    // Check for duplicates by date
    const duplicate = this.holidays.find(h => Holiday.isSameDate(h, holiday));
    if (duplicate) {
      throw HolidayServiceError.create(
        HolidayServiceError.types.DUPLICATE_HOLIDAY,
        new Error(`Holiday already exists on ${holiday.dateString}: ${duplicate.name}`)
      );
    }

    this.holidays.push(holiday);
    return this;
  }

  // Remove holiday by id
  remove(id) {
    const index = this.holidays.findIndex(h => h.id === id);
    if (index !== -1) {
      this.holidays.splice(index, 1);
    }
    return this;
  }

  // Update holiday by id
  update(id, updatedHoliday) {
    if (!(updatedHoliday instanceof Holiday)) {
      throw new Error('Can only update with Holiday instance');
    }

    const validation = updatedHoliday.validate();
    if (!validation.isValid) {
      throw HolidayServiceError.create(
        HolidayServiceError.types.DATA_CORRUPTION,
        new Error(validation.error)
      );
    }

    const index = this.holidays.findIndex(h => h.id === id);
    if (index !== -1) {
      this.holidays[index] = updatedHoliday;
    }
    return this;
  }

  // Get all holidays
  getAll() {
    return [...this.holidays];
  }

  // Get holidays sorted by date
  getSorted(ascending = true) {
    return Holiday.sortByDate([...this.holidays], ascending);
  }

  // Get holidays by year
  getByYear(year) {
    return Holiday.filterByYear(this.holidays, year);
  }

  // Get holidays by month
  getByMonth(year, month) {
    return Holiday.filterByMonth(this.holidays, year, month);
  }

  // Get holidays in date range
  getByDateRange(startDate, endDate) {
    return Holiday.filterByDateRange(this.holidays, startDate, endDate);
  }

  // Find holiday by id
  findById(id) {
    return this.holidays.find(h => h.id === id);
  }

  // Clear all holidays
  clear() {
    this.holidays = [];
    return this;
  }

  // Get count
  get count() {
    return this.holidays.length;
  }

  // Check if empty
  get isEmpty() {
    return this.holidays.length === 0;
  }
}

export default Holiday;